import { useState, useEffect, useCallback } from 'react';

const sitesImg: Record<string, string> = {
    us: 'https://websiteoss.ecoflow.com/upload/image/1754641710288/US.jpg',
    ca: 'https://websiteoss.ecoflow.com/upload/image/1754641722358/CA.jpg',
    eu: 'https://websiteoss.ecoflow.com/upload/image/1754650556921/EU.jpg',
    uk: 'https://websiteoss.ecoflow.com/upload/image/1754650750722/UK.jpg',
    de: 'https://websiteoss.ecoflow.com/upload/image/1754642011467/DE.jpg',
    fr: 'https://websiteoss.ecoflow.com/upload/image/1754650556921/EU.jpg',
    es: 'https://websiteoss.ecoflow.com/upload/image/1754650556921/EU.jpg',
    it: 'https://websiteoss.ecoflow.com/upload/image/1754650556921/EU.jpg',
    au: 'https://websiteoss.ecoflow.com/upload/image/1754643199882/AU.jpg',
    za: 'https://websiteoss.ecoflow.com/upload/image/1754642138251/ZA.jpg',
    jp: 'https://websiteoss.ecoflow.com/upload/image/1754633625299/JP.jpg',
    kr: 'https://websiteoss.ecoflow.com/upload/image/1754452851861/KR.jpg',
    br: 'https://websiteoss.ecoflow.com/upload/image/1754646189610/BR.jpg',
    mx: 'https://websiteoss.ecoflow.com/upload/image/1754642091521/MX.jpg',
    ph: 'https://websiteoss.ecoflow.com/upload/image/1754642107114/PH.jpg',
    sa: 'https://websiteoss.ecoflow.com/upload/image/1754642117177/SA.jpg',
};

const sitesWebp: Record<string, string> = {
    us: 'https://websiteoss.ecoflow.com/upload/image/1754641172372/US.webp',
    ca: 'https://websiteoss.ecoflow.com/upload/image/1754641297432/CA.webp',
    eu: 'https://websiteoss.ecoflow.com/upload/image/1754650741559/EU.webp',
    uk: 'https://websiteoss.ecoflow.com/upload/image/1754650797548/UK.webp',
    de: 'https://websiteoss.ecoflow.com/upload/image/1754641311954/DE.webp',
    fr: 'https://websiteoss.ecoflow.com/upload/image/1754650741559/EU.webp',
    es: 'https://websiteoss.ecoflow.com/upload/image/1754650741559/EU.webp',
    it: 'https://websiteoss.ecoflow.com/upload/image/1754650741559/EU.webp',
    au: 'https://websiteoss.ecoflow.com/upload/image/1754643232512/AU.webp',
    za: 'https://websiteoss.ecoflow.com/upload/image/1754641464021/ZA.webp',
    jp: 'https://websiteoss.ecoflow.com/upload/image/1754641317105/JP.webp',
    kr: 'https://websiteoss.ecoflow.com/upload/image/1754641327123/KR.webp',
    br: 'https://websiteoss.ecoflow.com/upload/image/1754641303428/BR.webp',
    mx: 'https://websiteoss.ecoflow.com/upload/image/1754641367684/MX.webp',
    ph: 'https://websiteoss.ecoflow.com/upload/image/1754641379355/PH.webp',
    sa: 'https://websiteoss.ecoflow.com/upload/image/1754641416950/SA.webp',
};

const defaultBackground = '#ffffff';

const imageCache = new Map<string, boolean>();

// WebP 支持检测函数
const checkWebPSupport = (): Promise<boolean> => {
    return new Promise((resolve) => {
        const webP = new Image();
        webP.onload = webP.onerror = () => {
            resolve(webP.height === 2);
        };
        webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    });
};

// 缓存 WebP 支持检测结果
let webpSupportCache: boolean | null = null;

const preloadImage = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (imageCache.has(src)) {
            resolve();
            return;
        }

        const img = new Image();
        img.onload = () => {
            imageCache.set(src, true);
            resolve();
        };
        img.onerror = reject;
        img.src = src;
    });
};

export const usePageBackground = (site: string) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [backgroundImage, setBackgroundImage] = useState<string>('');
    const [imageUrl, setImageUrl] = useState<string>('');

    const startAnimation = useCallback(() => {
        setIsAnimating(true);
        setTimeout(() => {
            setIsLoaded(true);
        }, 100);
    }, []);

    useEffect(() => {
        let isMounted = true;

        const initializeImage = async () => {
            // 检测 WebP 支持
            if (webpSupportCache === null) {
                webpSupportCache = await checkWebPSupport();
            }

            // 根据 WebP 支持情况选择图片 URL
            const selectedImageUrl = webpSupportCache ? sitesWebp[site] || sitesWebp['us'] : sitesImg[site] || sitesImg['us'];

            if (!isMounted) return;

            setImageUrl(selectedImageUrl);
            setBackgroundImage(selectedImageUrl);

            if (imageCache.has(selectedImageUrl)) {
                // 如果已缓存，立即开始动画
                startAnimation();
                return;
            }

            preloadImage(selectedImageUrl)
                .then(() => {
                    if (isMounted) {
                        startAnimation();
                    }
                })
                .catch(() => {
                    if (isMounted) {
                        setIsLoaded(true);
                    }
                });
        };

        initializeImage();

        return () => {
            isMounted = false;
        };
    }, [site, startAnimation]);

    return {
        backgroundImage,
        isLoaded,
        isAnimating,
        defaultBackground,
        imageUrl,
    };
};
